/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useFlags } from "launchdarkly-react-client-sdk";
import { INVOICING_RULE_CATEGORIES } from "@constants";
import utils from "@utils";
import { Modal, Tooltip } from "../../../../components";
import RatesRules from "./RatesRules";
import "./InvoicingRulesModal.scss";

const TABS = {
	BASERATES: "base-rate-tab",
	HOLIDAYRATES: "holiday-rate-tab",
};

const InvoicingRulesModal = props => {
	const { lang } = global;
	const {
		show,
		closeModal,
		invoiceRules,
		clients,
		ranks,
		paycodes,
		payCodesEnabled,
		userData,
		onCreate,
		onUpdate,
		onDelete,
		onRuleBulkUpdate,
		onBulkCreate,
	} = props;
	const [isMinimumPayrate, setIsMinimumPayrate] = useState(false);
	const [minimumPayRate, setMinimumPayRate] = useState(null);
	const [activeTab, setActiveTab] = useState(TABS.BASERATES);
	const [baseRules, setBaseRules] = useState({});
	const [holidaysRules, setHolidaysRules] = useState({});

	const { minimumPayrate } = useFlags();

	const convertToDate = str => {
		const date = new Date(str);
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, "0");
		const day = String(date.getDate()).padStart(2, "0");
		return `${year}-${month}-${day}`;
	};

	const mapRuleObject = rule => ({
		id: rule.Id,
		jobPaycodeId: rule.JobPaycodeId,
		clientId: rule.ClientId,
		officerRankId: rule.OfficerRankId,
		officerRate: rule.OfficerRate,
		paycodeId: rule.SetPaycode,
		fee: rule.AgencyFee,
		feeType: rule.FeeType,
		effectiveDateStart: rule.EffectiveDateStart
			? convertToDate(rule.EffectiveDateStart)
			: rule.EffectiveDateStart,
		effectiveDateEnd: rule.EffectiveDateEnd
			? convertToDate(rule.EffectiveDateEnd)
			: rule.EffectiveDateEnd,
		invoicingRuleCategoryId: rule.InvoicingRuleCategoryId,
		createDateUTC: rule.CreateDateUTC,
		updateDateUTC: rule.UpdateDateUTC,
		_editing: false,
		_showFee: false,
	});

	const sortRulesKeysDescending = obj => {
		const sortedKeys = Object.keys(obj).sort((a, b) => {
			const dateA = new Date(obj[a].rules[0].effectiveDateStart);
			const dateB = new Date(obj[b].rules[0].effectiveDateStart);
			return dateB - dateA;
		});

		const sortedObject = {};
		sortedKeys.forEach(key => {
			sortedObject[key] = obj[key];
		});

		return sortedObject;
	};

	const sortRules = rules => {
		const getDate = rule => {
			const date = rule.updateDateUTC || rule.createDateUTC;
			return utils.parseUTCDateToTimeZone(date, "UTC");
		};
		return rules.sort((a, b) => getDate(b) - getDate(a));
	};

	const groupInvoicingRules = (categoryId, rules) => {
		let groupedRulesAux;
		if (categoryId === INVOICING_RULE_CATEGORIES.BASERATES) {
			groupedRulesAux = { ...baseRules };
		} else {
			groupedRulesAux = { ...holidaysRules };
		}

		return rules.reduce((acc, obj) => {
			const key = `${obj.effectiveDateStart.split("T")[0]} - ${obj.effectiveDateEnd.split("T")[0]}`;

			// eslint-disable-next-line no-prototype-builtins
			if (!groupedRulesAux.hasOwnProperty(key)) {
				groupedRulesAux[key] = {
					rules: [],
					_editing: false,
					_collapsed: true,
					_newRule: {},
				};
			}

			const existingGroup = groupedRulesAux[key];
			const existingRuleIndex = existingGroup.rules.findIndex(r => r.id === obj.id);

			if (existingRuleIndex !== -1) {
				existingGroup.rules[existingRuleIndex] = obj;
			} else {
				existingGroup.rules.push(obj);
			}

			existingGroup.rules = sortRules(existingGroup.rules);
			acc[key] = existingGroup;
			return acc;
		}, {});
	};

	const sortAndSetBaseRates = bRules => {
		if (bRules == null || bRules.length === 0) {
			setBaseRules({});
			return;
		}
		const bRulesAux = bRules.map(r => mapRuleObject(r));
		const bRulesGrouped = groupInvoicingRules(INVOICING_RULE_CATEGORIES.BASERATES, bRulesAux);
		setBaseRules(sortRulesKeysDescending(bRulesGrouped));
	};

	const sortAndSetHolidaysRates = hRules => {
		if (hRules == null || hRules.length === 0) {
			setHolidaysRules({});
			return;
		}
		const hRulesAux = hRules.map(r => mapRuleObject(r));
		const hRulesGrouped = groupInvoicingRules(
			INVOICING_RULE_CATEGORIES.HOLIDAYRATES,
			hRulesAux,
		);
		setHolidaysRules(sortRulesKeysDescending(hRulesGrouped));
	};

	const categorizeInvoicingRules = () => {
		const bRules = invoiceRules.filter(
			item => item.InvoicingRuleCategoryId === INVOICING_RULE_CATEGORIES.BASERATES,
		);
		const hRules = invoiceRules.filter(
			item => item.InvoicingRuleCategoryId === INVOICING_RULE_CATEGORIES.HOLIDAYRATES,
		);
		sortAndSetBaseRates(bRules);
		sortAndSetHolidaysRates(hRules);

		if (minimumPayrate) {
			const mRule = invoiceRules.find(
				item => item.InvoicingRuleCategoryId === INVOICING_RULE_CATEGORIES.MINIMUMPAYRATES,
			);
			if (mRule) {
				setMinimumPayRate({
					id: mRule.Id,
					jobPaycodeId: mRule.JobPaycodeId,
					clientId: mRule.ClientId,
					officerRankId: mRule.OfficerRankId,
					officerRate: mRule.OfficerRate,
					paycodeId: mRule.SetPaycode,
					fee: mRule.AgencyFee,
					feeType: mRule.FeeType,
					invoicingRuleCategoryId: mRule.InvoicingRuleCategoryId,
					_editing: false,
				});
				setIsMinimumPayrate(true);
			}
		}
	};

	useEffect(() => {
		if (invoiceRules == null || invoiceRules.length === 0) {
			setMinimumPayRate(null);
		} else {
			categorizeInvoicingRules();
		}
	}, [show, invoiceRules]);

	const validatePositiveNumericValue = val => {
		if (val === "") return true;
		if (!val.match(/^\d*\.?\d{0,2}$/g)) {
			return false;
		}
		if (parseFloat(val) < 0) {
			return false;
		}
		return true;
	};

	const validateMinimumPayRateRule = () => {
		if (minimumPayRate == null) return false;
		if (
			(minimumPayRate.officerRate == null || minimumPayRate.officerRate === "") &&
			(minimumPayRate.fee == null ||
				minimumPayRate.fee === "" ||
				minimumPayRate.feeType == null ||
				minimumPayRate.feeType == null)
		) {
			return false;
		}

		return true;
	};

	const buildEmptyMinimumPayRate = () => {
		setMinimumPayRate({
			jobPaycodeId: null,
			clientId: null,
			officerRankId: null,
			officerRate: null,
			paycodeId: null,
			fee: null,
			feeType: null,
			invoicingRuleCategoryId: INVOICING_RULE_CATEGORIES.MINIMUMPAYRATES,
			_editing: true,
		});
	};

	const handleIsMinimumPayRate = e => {
		if (!e.checked) {
			const callback = () => {
				setIsMinimumPayrate(false);
				setMinimumPayRate(null);
			};

			if (minimumPayRate?.id) {
				onDelete(minimumPayRate.id, callback);
			} else {
				callback();
			}
			return;
		}

		setIsMinimumPayrate(true);
		buildEmptyMinimumPayRate();
	};

	const handleMinimumPayRate = e => {
		let isRateValid = true;
		isRateValid = validatePositiveNumericValue(e.value);
		if (isRateValid) {
			setMinimumPayRate({ ...minimumPayRate, officerRate: e.value });
		}
	};

	const changeTab = ({ target: { name } }) => {
		setActiveTab(name);
	};

	const toggleMinimumPayRateEdit = () => {
		const mRuleAux = { ...minimumPayRate };
		mRuleAux._editing = !mRuleAux._editing;
		mRuleAux.originalState = { ...minimumPayRate };
		setMinimumPayRate(mRuleAux);
	};

	const cancelToggleMinimumPayRate = () => {
		if (!minimumPayRate.id) {
			setIsMinimumPayrate(false);
			setMinimumPayRate(null);
			return;
		}
		const mRuleAux = { ...minimumPayRate };
		setMinimumPayRate(mRuleAux.originalState);
	};

	const saveMinimumPayRate = () => {
		const callback = () => {
			const mRuleAux = { ...minimumPayRate };
			mRuleAux._editing = false;
			setMinimumPayRate(mRuleAux);
		};

		if (!minimumPayRate.id) {
			onCreate({
				ClientId: minimumPayRate.clientId === -1 ? null : minimumPayRate.clientId,
				OrganizationId: userData.Organization.Id,
				OfficerRankId:
					minimumPayRate.officerRankId === -1 ? null : minimumPayRate.officerRankId,
				OfficerRate: minimumPayRate.officerRate,
				JobPaycodeId:
					minimumPayRate.jobPaycodeId === -1 ? null : minimumPayRate.jobPaycodeId,
				SetPaycode: minimumPayRate.paycodeId,
				AgencyFee: minimumPayRate.fee,
				FeeType: minimumPayRate.feeType,
				InvoicingRuleCategoryId: INVOICING_RULE_CATEGORIES.MINIMUMPAYRATES,
			});
			return;
		}

		onUpdate(
			{
				Id: minimumPayRate.id,
				ClientId: minimumPayRate.clientId === -1 ? null : minimumPayRate.clientId,
				OrganizationId: userData.Organization.Id,
				OfficerRankId:
					minimumPayRate.officerRankId === -1 ? null : minimumPayRate.officerRankId,
				OfficerRate: minimumPayRate.officerRate,
				JobPaycodeId:
					minimumPayRate.jobPaycodeId === -1 ? null : minimumPayRate.jobPaycodeId,
				SetPaycode: minimumPayRate.paycodeId,
				AgencyFee: minimumPayRate.fee,
				InvoicingRuleCategoryId: INVOICING_RULE_CATEGORIES.MINIMUMPAYRATES,
			},
			callback,
		);
	};

	return (
		<Modal
			id="invoice-rule-modal"
			dialogClassName="invoice-rule-dialog"
			show={show}
			size="lg"
			onHide={() => closeModal()}
			closeModal={() => closeModal()}
		>
			<Modal.Header
				closeButton
				className="hidden-print"
			>
				<Modal.Title>Invoicing Rules</Modal.Title>
			</Modal.Header>
			<Modal.Body className="container-fluid">
				<div style={{ marginLeft: "15px", marginRight: "15px" }}>
					<h5>{lang.t("Manage Rates")}</h5>
					<If condition={minimumPayrate}>
						<div className="row minimum-pay-rate-row">
							<div className="col-md-4">
								<label htmlFor="minimum-payrate">
									<input
										type="checkbox"
										name="minimum-payrate"
										id="minimum-payrate"
										checked={isMinimumPayrate}
										onChange={e => handleIsMinimumPayRate(e.target)}
									/>
									{lang.t("Apply officer minimum payrate")}
								</label>
								<Tooltip
									className="tool-tip"
									content={lang.t("Minimum Pay Rates Checkbox Tooltip")}
								>
									<i className="hidden-xs rkf rkf-question-solid tooltip-icon" />
								</Tooltip>
							</div>
							<If condition={isMinimumPayrate}>
								<If condition={minimumPayRate?._editing}>
									<div className="col-xs-4 col-md-2">
										<label htmlFor="minimum-rate">{lang.t("Rate")}*</label>
										<div className="input-group">
											<div className="input-group-addon">$</div>
											<input
												id="minimum-rate"
												name="minimum-rate"
												className="form-control"
												type="text"
												placeholder="Rate"
												value={minimumPayRate.officerRate}
												onChange={e => handleMinimumPayRate(e.target)}
											/>
										</div>
									</div>
									<div className="col-xs-10 col-md-2 edit-rule-buttons">
										<button
											id="cancel-minimum-pay-rate-edit"
											className="btn danger"
											type="button"
											onClick={() => cancelToggleMinimumPayRate()}
										>
											{lang.t("Cancel")}
										</button>
										<button
											id="save-minimum-pay-rate"
											className="btn primary"
											type="button"
											disabled={validateMinimumPayRateRule() === false}
											onClick={() => saveMinimumPayRate()}
										>
											{lang.t("Save")}
										</button>
									</div>
								</If>
								<If condition={!minimumPayRate?._editing}>
									<div className="col-xs-12 col-md-3">
										<div>
											<span>
												<strong>{lang.t("Rate")}: </strong>
											</span>
											<span>
												{utils.currencyFormatter(
													minimumPayRate?.officerRate,
												)}
											</span>
											<div
												style={{ display: "inline-block", paddingLeft: 10 }}
												className="invoice-form-padding"
											>
												<button
													style={{ paddingLeft: 0 }}
													type="button"
													className="btn btn-link"
													onClick={() => toggleMinimumPayRateEdit()}
												>
													<i className="glyphicon glyphicon-pencil" />
												</button>
											</div>
										</div>
									</div>
								</If>
							</If>
						</div>
					</If>
					<If condition={payCodesEnabled}>
						<div>
							<ul
								className="row nav tabs"
								role="presentation"
							>
								<li
									role="presentation"
									className={`col-xs-5 col-xs-offset-1 col-sm-3 col-sm-offset-3 col-md-2 col-md-offset-4${
										activeTab === TABS.BASERATES ? " active" : ""
									}`}
								>
									<button
										type="button"
										role="tab"
										name={TABS.BASERATES}
										className="btn-block"
										onClick={changeTab}
									>
										{lang.t("Base Rates")}
									</button>
								</li>
								<li
									role="presentation"
									className={`col-xs-5 col-sm-3 col-md-2${activeTab === TABS.HOLIDAYRATES ? " active" : ""}`}
								>
									<button
										type="button"
										role="tab"
										name={TABS.HOLIDAYRATES}
										className="btn-block"
										onClick={changeTab}
									>
										{lang.t("Holiday Rates")}
									</button>
								</li>
							</ul>
						</div>
						<div>
							<small>
								{lang.t("Define invoice changes to apply after a job is worked")}
							</small>
						</div>
						<If condition={activeTab === TABS.BASERATES}>
							<RatesRules
								invoiceRules={baseRules}
								clients={clients}
								ranks={ranks}
								paycodes={paycodes}
								userData={userData}
								onCreate={onCreate}
								onUpdate={onUpdate}
								onDelete={onDelete}
								onRuleBulkUpdate={onRuleBulkUpdate}
								onBulkCreate={onBulkCreate}
								onSetInvoicingRule={setBaseRules}
							/>
						</If>
						<If condition={activeTab === TABS.HOLIDAYRATES}>
							<RatesRules
								invoiceRules={holidaysRules}
								clients={clients}
								ranks={ranks}
								paycodes={paycodes}
								userData={userData}
								onCreate={onCreate}
								onUpdate={onUpdate}
								onDelete={onDelete}
								onRuleBulkUpdate={onRuleBulkUpdate}
								baseRatesRules={false}
								onBulkCreate={onBulkCreate}
								onSetInvoicingRule={setHolidaysRules}
							/>
						</If>
					</If>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<button
					id="button-go-back"
					className="btn btn-link hidden-print"
					type="button"
					onClick={() => closeModal()}
				>
					{lang.t("Close")}
				</button>
				<button
					id="button-print-table"
					className="btn btn-primary hidden-print"
					type="button"
					onClick={() => closeModal()}
				>
					{lang.t("Done")}
				</button>
			</Modal.Footer>
		</Modal>
	);
};

InvoicingRulesModal.propTypes = {
	show: PropTypes.bool,
	closeModal: PropTypes.func,
	invoiceRules: PropTypes.array,
	clients: PropTypes.array,
	ranks: PropTypes.array,
	paycodes: PropTypes.array,
	payCodesEnabled: PropTypes.bool,
	userData: PropTypes.object,
	onCreate: PropTypes.func.isRequired,
	onUpdate: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	onRuleBulkUpdate: PropTypes.func.isRequired,
	onBulkCreate: PropTypes.func.isRequired,
};

InvoicingRulesModal.defaultProps = {
	show: false,
	invoiceRules: [],
	clients: [],
	ranks: [],
	paycodes: [],
	closeModal: () => {},
	payCodesEnabled: false,
	userData: {},
};

export default InvoicingRulesModal;
